import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  FileSpreadsheet,
  Filter,
  Loader2,
  Search,
  X,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import * as XLSX from 'xlsx';
import {
  getAdminTransactionsSummary,
  type AdminTransactionsSummaryResponse,
  type AdminTransactionsSummaryUserItem,
} from '../../services/api/user.service';

type RangeOption = '7d' | '30d' | '90d' | 'month' | 'all';
type SummaryTab = 'analytics' | 'users';

const TOP_USERS_COUNT = 6;

type UserSortField =
  | 'fullName'
  | 'artistName'
  | 'accountNumber'
  | 'totalTransactions'
  | 'totalCredits'
  | 'totalDebits'
  | 'netChange'
  | 'lastTransactionDate';

type SortDir = 'asc' | 'desc';

type UserTableFilters = {
  search: string;
  fullName: string;
  artistName: string;
  accountNumber: string;
  accountStatus: string;
};

const RANGE_OPTIONS: Array<{ id: RangeOption; label: string }> = [
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
  { id: 'month', label: 'This month' },
  { id: 'all', label: 'All time' },
];

const DEFAULT_USER_FILTERS: UserTableFilters = {
  search: '',
  fullName: '',
  artistName: '',
  accountNumber: '',
  accountStatus: '',
};

function formatUsd(value: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value || 0);
}

function formatShortDate(iso: string | null) {
  if (!iso) return '—';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function getTypeBarColor(index: number) {
  const palette = [
    'bg-indigo-500',
    'bg-emerald-500',
    'bg-amber-500',
    'bg-sky-500',
    'bg-violet-500',
    'bg-rose-500',
  ];
  return palette[index % palette.length];
}

function getAccountDisplay(user: AdminTransactionsSummaryUserItem) {
  return user.accountNumber || user.accountId;
}

function getRangeLabel(range: RangeOption) {
  return RANGE_OPTIONS.find((option) => option.id === range)?.label || range;
}

function filterUsers(users: AdminTransactionsSummaryUserItem[], filters: UserTableFilters) {
  const search = filters.search.trim().toLowerCase();
  const fullName = filters.fullName.trim().toLowerCase();
  const artistName = filters.artistName.trim().toLowerCase();
  const accountNumber = filters.accountNumber.trim().toLowerCase();
  const accountStatus = filters.accountStatus.trim().toLowerCase();

  return users.filter((user) => {
    const accountDisplay = getAccountDisplay(user).toLowerCase();
    const status = (user.accountStatus || '').toLowerCase();

    if (search) {
      const haystack = [
        user.fullName,
        user.artistName,
        accountDisplay,
        user.accountId,
        status,
      ]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }

    if (fullName && !user.fullName.toLowerCase().includes(fullName)) return false;
    if (artistName && !user.artistName.toLowerCase().includes(artistName)) return false;
    if (accountNumber && !accountDisplay.includes(accountNumber) && !user.accountId.toLowerCase().includes(accountNumber)) {
      return false;
    }
    if (accountStatus && !status.includes(accountStatus)) return false;

    return true;
  });
}

function sortUsers(
  users: AdminTransactionsSummaryUserItem[],
  sortField: UserSortField,
  sortDir: SortDir,
) {
  const sorted = [...users].sort((left, right) => {
    let comparison = 0;

    switch (sortField) {
      case 'fullName':
        comparison = left.fullName.localeCompare(right.fullName);
        break;
      case 'artistName':
        comparison = left.artistName.localeCompare(right.artistName);
        break;
      case 'accountNumber':
        comparison = getAccountDisplay(left).localeCompare(getAccountDisplay(right));
        break;
      case 'totalTransactions':
        comparison = left.totalTransactions - right.totalTransactions;
        break;
      case 'totalCredits':
        comparison = left.totalCredits - right.totalCredits;
        break;
      case 'totalDebits':
        comparison = left.totalDebits - right.totalDebits;
        break;
      case 'netChange':
        comparison = left.netChange - right.netChange;
        break;
      case 'lastTransactionDate': {
        const leftTime = left.lastTransactionDate ? new Date(left.lastTransactionDate).getTime() : 0;
        const rightTime = right.lastTransactionDate ? new Date(right.lastTransactionDate).getTime() : 0;
        comparison = leftTime - rightTime;
        break;
      }
      default:
        comparison = 0;
    }

    return sortDir === 'asc' ? comparison : -comparison;
  });

  return sorted;
}

function buildTransactionsSummaryFilename(range: RangeOption) {
  const stamp = new Date().toISOString().slice(0, 10);
  return `transactions-summary-${range}-${stamp}.xlsx`;
}

function exportUsersToExcel(users: AdminTransactionsSummaryUserItem[], range: RangeOption) {
  const exportData = users.map((user) => ({
    User: user.fullName,
    Artist: user.artistName,
    Account: getAccountDisplay(user),
    'Account ID': user.accountId,
    Transactions: user.totalTransactions,
    Credits: user.totalCredits,
    Debits: user.totalDebits,
    Net: user.netChange,
    'Last activity': user.lastTransactionDate ? formatShortDate(user.lastTransactionDate) : '—',
  }));

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions Summary');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = buildTransactionsSummaryFilename(range);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function ShimmerBlock({ className = '' }: { className?: string }) {
  return (
    <div
      className={`relative overflow-hidden rounded-md bg-gray-200/70 ${className}`}
      aria-hidden="true"
    >
      <div className="absolute inset-0 summary-shimmer-wave bg-gradient-to-r from-transparent via-white/70 to-transparent" />
    </div>
  );
}

function AnalyticsTabShimmer() {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 mb-4">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
            <ShimmerBlock className="h-3 w-24" />
            <ShimmerBlock className="h-8 w-20 mt-3" />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4 xl:col-span-2">
          <ShimmerBlock className="h-4 w-56 mb-4" />
          <div className="flex gap-4 mb-4">
            <ShimmerBlock className="h-3 w-16" />
            <ShimmerBlock className="h-3 w-16" />
          </div>
          <ShimmerBlock className="h-56 w-full" />
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <ShimmerBlock className="h-4 w-40 mb-4" />
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index}>
                <div className="flex justify-between mb-1">
                  <ShimmerBlock className="h-3 w-28" />
                  <ShimmerBlock className="h-3 w-10" />
                </div>
                <ShimmerBlock className="h-2 w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex justify-between mb-4">
          <div>
            <ShimmerBlock className="h-4 w-44" />
            <ShimmerBlock className="h-3 w-24 mt-2" />
          </div>
          <ShimmerBlock className="h-3 w-28" />
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-8 gap-2 pb-2 border-b border-gray-100">
            {Array.from({ length: 8 }).map((_, index) => (
              <ShimmerBlock key={index} className="h-3 w-full" />
            ))}
          </div>
          {Array.from({ length: 6 }).map((_, rowIndex) => (
            <div key={rowIndex} className="grid grid-cols-8 gap-2">
              {Array.from({ length: 8 }).map((_, colIndex) => (
                <ShimmerBlock key={colIndex} className="h-4 w-full" />
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function UsersTabShimmer() {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
        <div>
          <ShimmerBlock className="h-4 w-48" />
          <ShimmerBlock className="h-3 w-36 mt-2" />
        </div>
        <div className="flex flex-wrap gap-2">
          <ShimmerBlock className="h-8 w-52" />
          <ShimmerBlock className="h-8 w-20" />
          <ShimmerBlock className="h-8 w-28" />
        </div>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-8 gap-2 pb-2 border-b border-gray-100">
          {Array.from({ length: 8 }).map((_, index) => (
            <ShimmerBlock key={index} className="h-3 w-full" />
          ))}
        </div>
        {Array.from({ length: 10 }).map((_, rowIndex) => (
          <div key={rowIndex} className="grid grid-cols-8 gap-2">
            {Array.from({ length: 8 }).map((_, colIndex) => (
              <ShimmerBlock key={colIndex} className="h-4 w-full" />
            ))}
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-4 pt-3 border-t border-gray-100">
        <ShimmerBlock className="h-8 w-32" />
        <ShimmerBlock className="h-8 w-48" />
      </div>
    </div>
  );
}

function TabContentError({ message }: { message: string }) {
  return (
    <div className="bg-white rounded-lg border border-red-200 p-6 text-center">
      <p className="text-sm text-red-600 font-family-messina-sans">{message}</p>
    </div>
  );
}

const AccountTransactionsSummaryPage: React.FC = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState<RangeOption>('30d');
  const [activeTab, setActiveTab] = useState<SummaryTab>('analytics');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<AdminTransactionsSummaryResponse | null>(null);

  const [showUserFilters, setShowUserFilters] = useState(false);
  const [userFilters, setUserFilters] = useState<UserTableFilters>(DEFAULT_USER_FILTERS);
  const [sortField, setSortField] = useState<UserSortField>('netChange');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getAdminTransactionsSummary(range);
        setSummary(response);
      } catch (err: any) {
        setError(err?.message || 'Failed to load summary');
        setSummary(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [range]);

  useEffect(() => {
    setCurrentPage(1);
  }, [range, userFilters, sortField, sortDir, itemsPerPage]);

  const filteredUsers = useMemo(() => {
    if (!summary) return [];
    return filterUsers(summary.users, userFilters);
  }, [summary, userFilters]);

  const sortedUsers = useMemo(
    () => sortUsers(filteredUsers, sortField, sortDir),
    [filteredUsers, sortField, sortDir],
  );

  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / itemsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStart = (safeCurrentPage - 1) * itemsPerPage;
  const pageUsers = sortedUsers.slice(pageStart, pageStart + itemsPerPage);

  const accountStatusOptions = useMemo(() => {
    if (!summary) return [];
    return Array.from(
      new Set(summary.users.map((user) => user.accountStatus).filter(Boolean) as string[]),
    ).sort();
  }, [summary]);

  const activeFilterCount = Object.values(userFilters).filter((value) => value.trim()).length;
  const topUsers = useMemo(() => (summary?.users || []).slice(0, TOP_USERS_COUNT), [summary]);
  const rangeLabel = getRangeLabel(range);

  const handleSort = (field: UserSortField) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortDir(field === 'fullName' || field === 'artistName' || field === 'accountNumber' ? 'asc' : 'desc');
  };

  const clearUserFilters = () => {
    setUserFilters(DEFAULT_USER_FILTERS);
  };

  const renderSortIcon = (field: UserSortField) => {
    if (sortField !== field) {
      return <ChevronDown size={12} className="text-gray-300" />;
    }
    return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  const handleExportExcel = async () => {
    if (isExporting || sortedUsers.length === 0) return;

    setIsExporting(true);
    try {
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });
      exportUsersToExcel(sortedUsers, range);
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 500);
      });
    } finally {
      setIsExporting(false);
    }
  };

  const showTabShimmer = loading;
  const showTabError = !loading && Boolean(error);
  const showTabContent = !loading && !error && summary;

  return (
    <div className="min-h-screen bg-[#F8F7F2]">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <button
              type="button"
              onClick={() => navigate('/customer-details')}
              className="mb-3 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors font-family-messina-sans"
            >
              <ArrowLeft size={16} />
              Back
            </button>
            <h1 className="text-[1.875rem] font-regular text-gray-900 font-family-messina-modern">Executive Overview</h1>
            <p className="text-sm text-gray-500 font-family-messina-sans mt-1">
              {summary
                ? `All users · ${summary.overview.activeAccounts} accounts · Updated ${formatShortDate(summary.asOf)}`
                : 'Platform-wide transaction summary'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {RANGE_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setRange(option.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                range === option.id
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="flex gap-1 mb-5 border-b border-gray-200">
          {([
            { id: 'analytics' as const, label: 'Analytics' },
            { id: 'users' as const, label: 'All Users' },
          ]).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors font-family-messina-sans ${
                activeTab === tab.id
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'analytics' && (
          showTabShimmer ? (
            <AnalyticsTabShimmer />
          ) : showTabError ? (
            <TabContentError message={error || 'Failed to load summary'} />
          ) : showTabContent ? (
          <>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 mb-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">Active accounts</p>
            <p className="text-2xl font-semibold mt-2 tabular-nums">{summary.overview.activeAccounts}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">Total transactions</p>
            <p className="text-2xl font-semibold mt-2 tabular-nums">{summary.overview.totalTransactions.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">Total credits</p>
            <p className="text-2xl font-semibold mt-2 tabular-nums">{formatUsd(summary.overview.totalCredits)}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">Total debits</p>
            <p className="text-2xl font-semibold mt-2 tabular-nums">{formatUsd(summary.overview.totalDebits)}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">Net change</p>
            <p className="text-2xl font-semibold mt-2 tabular-nums">{formatUsd(summary.overview.netChange)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4 xl:col-span-2">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 font-family-messina-sans">Volume trend (credits vs debits)</h3>
            <div className="flex items-center gap-4 mb-3 text-xs text-gray-600 font-family-messina-sans">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />
                Credits
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-rose-400" />
                Debits
              </span>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary.trend} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ececec" />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) => `$${Number(value).toLocaleString()}`}
                  />
                  <Tooltip
                    formatter={(value: number | string | undefined, name?: string) => [formatUsd(Number(value || 0)), name || 'Value']}
                    cursor={{ fill: 'rgba(0, 0, 0, 0.03)' }}
                    contentStyle={{ borderRadius: 8, borderColor: '#e5e7eb', fontSize: 12 }}
                  />
                  <Bar dataKey="credits" name="Credits" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="debits" name="Debits" fill="#fb7185" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 font-family-messina-sans">By transaction type</h3>
            <div className="space-y-3">
              {summary.typeBreakdown.slice(0, 6).map((item, index) => (
                <div key={item.type}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-700 inline-flex items-center gap-1.5">
                      <span className={`w-2.5 h-2.5 rounded-sm ${getTypeBarColor(index)}`} />
                      {item.type}
                    </span>
                    <span className="text-gray-500 tabular-nums">{item.percentage.toFixed(1)}%</span>
                  </div>
                  <div className="h-2 rounded bg-gray-100 overflow-hidden">
                    <div className={`h-full rounded ${getTypeBarColor(index)}`} style={{ width: `${Math.min(100, item.percentage)}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-2">
              {summary.typeBreakdown.slice(0, 6).map((item, index) => (
                <div key={item.type} className="text-[11px] text-gray-600 inline-flex items-center gap-1.5">
                  <span className={`w-2.5 h-2.5 rounded-sm ${getTypeBarColor(index)}`} />
                  <span className="truncate">{item.type}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 font-family-messina-sans">Top users by net volume</h3>
              <p className="text-xs text-gray-500 mt-1 font-family-messina-sans">
                Top {TOP_USERS_COUNT} · {rangeLabel}
              </p>
            </div>
            {summary.users.length > TOP_USERS_COUNT && (
              <button
                type="button"
                onClick={() => setActiveTab('users')}
                className="text-xs font-medium text-gray-700 hover:text-gray-900 underline underline-offset-2 font-family-messina-sans shrink-0"
              >
                View all {summary.users.length.toLocaleString()} users
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-2 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide">User</th>
                  <th className="text-left py-2 px-2 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide">Artist</th>
                  <th className="text-left py-2 px-2 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide">Account</th>
                  <th className="text-right py-2 px-2 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide">Transactions</th>
                  <th className="text-right py-2 px-2 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide">Credits</th>
                  <th className="text-right py-2 px-2 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide">Debits</th>
                  <th className="text-right py-2 px-2 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide">Net</th>
                  <th className="text-left py-2 px-2 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {topUsers.map((user) => (
                  <tr key={`${user.accountId}-${user.userId}`} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-2">{user.fullName}</td>
                    <td className="py-2 px-2">{user.artistName}</td>
                    <td className="py-2 px-2 tabular-nums">{getAccountDisplay(user)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{user.totalTransactions.toLocaleString()}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{formatUsd(user.totalCredits)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{formatUsd(user.totalDebits)}</td>
                    <td className="py-2 px-2 text-right tabular-nums font-semibold">{formatUsd(user.netChange)}</td>
                    <td className="py-2 px-2">{formatShortDate(user.lastTransactionDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {summary.meta.accountFailures > 0 && (
            <p className="text-xs text-amber-700 mt-3">
              {summary.meta.accountFailures} account(s) failed to load and were excluded from totals.
            </p>
          )}
        </div>
          </>
          ) : null
        )}

        {activeTab === 'users' && (
          showTabShimmer ? (
            <UsersTabShimmer />
          ) : showTabError ? (
            <TabContentError message={error || 'Failed to load summary'} />
          ) : showTabContent ? (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 font-family-messina-sans">All users by net volume</h3>
              <p className="text-xs text-gray-500 mt-1 font-family-messina-sans">
                {sortedUsers.length.toLocaleString()} user{sortedUsers.length === 1 ? '' : 's'} · {rangeLabel}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} active` : ''}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={userFilters.search}
                  onChange={(event) => setUserFilters((prev) => ({ ...prev, search: event.target.value }))}
                  placeholder="Search users..."
                  className="pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-md w-52 focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowUserFilters((prev) => !prev)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded-md transition-colors ${
                  showUserFilters || activeFilterCount > 0
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
                }`}
              >
                <Filter size={14} />
                Filters
                {activeFilterCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-white text-gray-900 text-[10px] font-semibold">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={handleExportExcel}
                disabled={isExporting || sortedUsers.length === 0}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-300 rounded-md bg-white text-gray-700 hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExporting ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
                {isExporting ? 'Exporting…' : 'Export Excel'}
              </button>
            </div>
          </div>

          {showUserFilters && (
            <div className="mb-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-1">User</label>
                  <input
                    type="text"
                    value={userFilters.fullName}
                    onChange={(event) => setUserFilters((prev) => ({ ...prev, fullName: event.target.value }))}
                    placeholder="Filter by name"
                    className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-1">Artist</label>
                  <input
                    type="text"
                    value={userFilters.artistName}
                    onChange={(event) => setUserFilters((prev) => ({ ...prev, artistName: event.target.value }))}
                    placeholder="Filter by artist"
                    className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-1">Account</label>
                  <input
                    type="text"
                    value={userFilters.accountNumber}
                    onChange={(event) => setUserFilters((prev) => ({ ...prev, accountNumber: event.target.value }))}
                    placeholder="Filter by account"
                    className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-1">Status</label>
                  <select
                    value={userFilters.accountStatus}
                    onChange={(event) => setUserFilters((prev) => ({ ...prev, accountStatus: event.target.value }))}
                    className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-gray-400"
                  >
                    <option value="">All statuses</option>
                    {accountStatusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={clearUserFilters}
                  className="mt-3 inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                >
                  <X size={12} />
                  Clear filters
                </button>
              )}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  {[
                    { field: 'fullName' as const, label: 'User', align: 'left' },
                    { field: 'artistName' as const, label: 'Artist', align: 'left' },
                    { field: 'accountNumber' as const, label: 'Account', align: 'left' },
                    { field: 'totalTransactions' as const, label: 'Transactions', align: 'right' },
                    { field: 'totalCredits' as const, label: 'Credits', align: 'right' },
                    { field: 'totalDebits' as const, label: 'Debits', align: 'right' },
                    { field: 'netChange' as const, label: 'Net', align: 'right' },
                    { field: 'lastTransactionDate' as const, label: 'Last activity', align: 'left' },
                  ].map((column) => (
                    <th
                      key={column.field}
                      className={`py-2 px-2 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide ${
                        column.align === 'right' ? 'text-right' : 'text-left'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => handleSort(column.field)}
                        className={`inline-flex items-center gap-1 hover:text-gray-800 ${
                          column.align === 'right' ? 'ml-auto' : ''
                        }`}
                      >
                        {column.label}
                        {renderSortIcon(column.field)}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageUsers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-sm text-gray-500">
                      No users match the current filters.
                    </td>
                  </tr>
                ) : (
                  pageUsers.map((user) => (
                    <tr key={`${user.accountId}-${user.userId}`} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-2">{user.fullName}</td>
                      <td className="py-2 px-2">{user.artistName}</td>
                      <td className="py-2 px-2 tabular-nums">{getAccountDisplay(user)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{user.totalTransactions.toLocaleString()}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatUsd(user.totalCredits)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatUsd(user.totalDebits)}</td>
                      <td className="py-2 px-2 text-right tabular-nums font-semibold">{formatUsd(user.netChange)}</td>
                      <td className="py-2 px-2">{formatShortDate(user.lastTransactionDate)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span>Rows per page</span>
              <select
                value={itemsPerPage}
                onChange={(event) => setItemsPerPage(Number(event.target.value))}
                className="px-2 py-1 border border-gray-300 rounded-md bg-white"
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-600">
                {sortedUsers.length === 0
                  ? 'Showing 0 users'
                  : `Showing ${pageStart + 1} to ${Math.min(pageStart + itemsPerPage, sortedUsers.length)} of ${sortedUsers.length} users`}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={safeCurrentPage <= 1}
                  className="p-1.5 rounded border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="text-xs text-gray-600 min-w-[4.5rem] text-center">
                  Page {safeCurrentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={safeCurrentPage >= totalPages}
                  className="p-1.5 rounded border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>

          {summary.meta.accountFailures > 0 && (
            <p className="text-xs text-amber-700 mt-3">
              {summary.meta.accountFailures} account(s) failed to load and were excluded from totals.
            </p>
          )}
        </div>
          ) : null
        )}
      </div>
      <style>{`
        @keyframes summary-shimmer-wave {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .summary-shimmer-wave {
          animation: summary-shimmer-wave 1.6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default AccountTransactionsSummaryPage;
